import { Link } from "react-router-dom";
import { usePublicMeta } from "@/lib/publicMeta";

/**
 * The page anyone sees at the root address before signing in.
 *
 * Every word comes from Platform settings. Centred rather than left-aligned,
 * with a soft wash of the brand colour behind the headline so the page doesn't
 * read as a blank sheet.
 */
export default function PublicHome() {
  const meta = usePublicMeta();
  const name = meta.platform_name || "Inventory";
  const c = meta.public?.landing || {};
  const points = (c.points || []).filter((p) => (p || "").trim()).slice(0, 3);
  const cta = c.cta || "Sign in";

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <header className="border-b border-line">
        {/* Full width — a header bar belongs at the page edges. The narrow
            column below is for reading, not for chrome. */}
        <div className="px-6 sm:px-10 py-4 flex items-center gap-3">
          {/* The wordmark goes home, as people expect it to. */}
          <Link to="/" className="flex items-center gap-3 group" aria-label={`${name} home`}>
            {/* The uploaded logo if there is one, the initial otherwise. */}
            {meta.logo ? (
              <img
                src={meta.logo}
                alt=""
                className="w-8 h-8 rounded-[10px] object-contain shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded-[10px] bg-brand flex items-center justify-center shrink-0">
                <span className="font-display text-white text-[15px] font-semibold leading-none">
                  {name[0]?.toUpperCase()}
                </span>
              </div>
            )}
            <span className="font-display text-[19px] font-semibold tracking-[-0.01em] text-ink group-hover:text-brand transition-colors">
              {name}
            </span>
          </Link>
          <Link
            to="/login"
            className="ml-auto text-[13px] font-medium text-brand hover:text-brand-hover"
          >
            {cta}
          </Link>
        </div>
      </header>

      <main className="flex-1">
        {/* A wash rather than a block — it fades out before the content below. */}
        <div className="bg-gradient-to-b from-brand/[0.07] to-transparent">
          <div className="max-w-[860px] mx-auto px-6 sm:px-8 pt-16 pb-14 sm:pt-20 text-center animate-rise">
            {c.headline && (
              <h1 className="font-display text-[36px] sm:text-[44px] leading-[1.1] font-semibold tracking-[-0.024em] text-ink max-w-[16ch] mx-auto">
                {c.headline}
              </h1>
            )}

            {c.subhead && (
              <p className="mt-5 text-[15.5px] leading-relaxed text-muted2 max-w-[46ch] mx-auto">
                {c.subhead}
              </p>
            )}

            <Link
              to="/login"
              className="inline-block mt-8 h-12 leading-[48px] px-8 rounded-xl bg-brand text-white font-medium hover:bg-brand-hover transition-colors"
            >
              {cta}
            </Link>
          </div>
        </div>

        {points.length > 0 && (
          <div className="max-w-[860px] mx-auto px-6 sm:px-8 pb-16">
            <div className="grid gap-8 sm:grid-cols-3 text-center">
              {points.map((point) => (
                <div key={point}>
                  <div className="h-0.5 w-6 bg-brand mx-auto mb-3" />
                  <p className="text-[13.5px] text-ink leading-relaxed">{point}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-line">
        <div className="px-6 sm:px-10 py-5">
          <p className="text-[12.5px] text-muted2 text-center">
            {c.footer || `${name} · ${new Date().getFullYear()}`}
          </p>
        </div>
      </footer>
    </div>
  );
}
